/**
 * ShiftChangePanel — the handover windows.
 *
 * One row per shift-change boundary, covering events within ±30 minutes of the
 * changeover instant. Boundaries are DERIVED by the proc from the campus's own
 * hour map, not assumed to be three: the settings editor lets any hour be any
 * shift, so a campus can legitimately have more.
 *
 * This measures a different failure from the metrics table's Handoff-Spanning
 * row, and both are here on purpose:
 *   - Handoff-Spanning  — an alert that outlived its shift.
 *   - This panel        — the changeover itself being chaotic, whoever answered.
 *
 * Each row's average is shown against the campus average, because the number
 * that matters is the ratio: "the Night→Day handover responds 2.4x slower than
 * this campus overall" is the sentence a manager acts on. A bare "6.1 min" is
 * not.
 */

import {
    DASH,
    formatCount,
    formatHourLabel,
    formatMinutes,
    shiftColor,
} from '../../../../utils/shiftFormat.js';

/** Ratio of a boundary's average against the campus average, e.g. "2.4x slower". */
function describeRatio(value, baseline) {
    if (value === null || value === undefined) return '';
    if (!baseline) return '';
    const ratio = Number(value) / Number(baseline);
    if (!Number.isFinite(ratio) || ratio <= 0) return '';
    /* Within 10% either way is not a finding, and calling it "1.0x slower" would
       invite someone to act on noise. */
    if (ratio > 0.9 && ratio < 1.1) return 'in line with the campus average';
    return ratio > 1
        ? `${ratio.toFixed(1)}× slower than the campus average`
        : `${(1 / ratio).toFixed(1)}× faster than the campus average`;
}

const ShiftChangePanel = ({ boundaries = [], summary = {} }) => {
    const campusAvg = summary.campus_avg_response_time;

    return (
        <div className="sr-card">
            <div className="sr-card__head">
                <div>
                    <h3 className="sr-card__title">Shift change windows</h3>
                    <p className="sr-card__sub">
                        Events within 30 minutes either side of a handover.
                        {(summary.handoff_events || 0) > 0 ? (
                            <>
                                {' '}
                                Separately,
                                {' '}
                                <strong>{formatCount(summary.handoff_events)}</strong>
                                {' '}
                                {summary.handoff_events === 1 ? 'alert was' : 'alerts were'}
                                {' '}
                                acknowledged in a different shift than the one it started in.
                            </>
                        ) : null}
                    </p>
                </div>
            </div>

            {boundaries.length ? (
                <div className="sr-table-wrap">
                    <table className="dp-table sr-boundaries">
                        <thead className="dp-thead">
                            <tr>
                                <th className="dp-th" scope="col">Handover</th>
                                <th className="dp-th dp-th--num" scope="col">At</th>
                                <th className="dp-th dp-th--num" scope="col">Events</th>
                                <th className="dp-th dp-th--num" scope="col">Avg Response (min)</th>
                                <th className="dp-th" scope="col">Compared with campus</th>
                            </tr>
                        </thead>
                        <tbody>
                            {boundaries.map((b) => {
                                const ratioText = describeRatio(b.avg_response_time, campusAvg);
                                const slower = b.avg_response_time !== null
                                    && b.avg_response_time !== undefined
                                    && campusAvg
                                    && Number(b.avg_response_time) / Number(campusAvg) >= 1.1;
                                return (
                                    <tr key={b.boundary_hour}>
                                        <td className="dp-td">
                                            <span className="sr-handover">
                                                <span
                                                    className="sr-swatch"
                                                    style={{ background: shiftColor(b.from_shift) }}
                                                    aria-hidden="true"
                                                />
                                                {b.from_shift || DASH}
                                                <i className="fe fe-arrow-right sr-handover__arrow" aria-hidden="true" />
                                                <span
                                                    className="sr-swatch"
                                                    style={{ background: shiftColor(b.to_shift) }}
                                                    aria-hidden="true"
                                                />
                                                {b.to_shift || DASH}
                                            </span>
                                        </td>
                                        <td className="dp-td dp-td--num">{formatHourLabel(b.boundary_hour)}</td>
                                        <td className="dp-td dp-td--num">{formatCount(b.total_events)}</td>
                                        <td className="dp-td dp-td--num">{formatMinutes(b.avg_response_time)}</td>
                                        <td className={`dp-td sr-td--ratio${slower ? ' sr-tone--bad' : ''}`}>
                                            {ratioText || DASH}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                /* Zero boundaries means every hour of the day belongs to one
                   shift — the state sp_createShifts seeds, where all 24 hours are
                   "Day". Saying so is more useful than an empty table, because
                   the fix is in shift settings, not in the report. */
                <p className="sr-empty">
                    This campus has no shift changes configured — every hour of the day is
                    assigned to a single shift. Set the shift hours in Settings to see
                    handover data here.
                </p>
            )}
        </div>
    );
};

export default ShiftChangePanel;
